import { useEffect, useState } from 'react';
import type { InterviewQuestion } from '../content/data';
import { useProgress, useProgressActions } from '../progress/progress-context';
import { MarkdownView } from './markdown-view';

export function InterviewQuestionCard({
  question,
  compact = false,
  readOnly = false
}: {
  question: InterviewQuestion;
  compact?: boolean;
  readOnly?: boolean;
}) {
  const progress = useProgress();
  const { setQuestionAttempt } = useProgressActions();
  const attempt = progress.questionAttempts[question.id] ?? { attempted: false, notes: '' };
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!attempt.attempted) setRevealed(false);
  }, [attempt.attempted]);

  return (
    <article className={`question-card ${compact ? 'question-card--compact' : ''}`}>
      <div className="question-heading">
        <span className="question-id">{question.id}</span>
        <h3>{question.prompt}</h3>
      </div>

      <label className="answer-check">
        <input
          type="checkbox"
          checked={attempt.attempted}
          disabled={readOnly}
          onChange={(event) => {
            const attempted = event.currentTarget.checked;
            if (!attempted) setRevealed(false);
            setQuestionAttempt(question.id, { ...attempt, attempted });
          }}
        />
        <span>I answered this aloud before checking the model answer.</span>
      </label>

      <details className="question-notes" open={!compact && Boolean(attempt.notes)}>
        <summary>Add private notes</summary>
        <label>
          <span className="sr-only">Notes for {question.id}</span>
          <textarea
            rows={compact ? 3 : 4}
            value={attempt.notes}
            disabled={readOnly}
            placeholder="Write the part you missed or want to say more clearly next time."
            onChange={(event) =>
              setQuestionAttempt(question.id, { ...attempt, notes: event.currentTarget.value })
            }
          />
        </label>
      </details>

      <button
        className="text-button"
        type="button"
        disabled={!attempt.attempted}
        onClick={() => setRevealed((current) => !current)}
      >
        {revealed ? 'Hide model answer' : 'Compare with model answer'}
      </button>
      {!attempt.attempted ? <p className="field-hint">Answer aloud first. The comparison button will then open.</p> : null}

      {revealed ? (
        <div className="model-answer">
          <MarkdownView markdown={question.modelAnswerMarkdown} />
        </div>
      ) : null}
    </article>
  );
}

export function InterviewPractice({ questions, readOnly = false }: { questions: InterviewQuestion[]; readOnly?: boolean }) {
  if (questions.length === 0) {
    return <p>No interview questions are assigned to this day.</p>;
  }

  return (
    <div className="question-list">
      {questions.map((question) => (
        <InterviewQuestionCard key={question.id} question={question} readOnly={readOnly} />
      ))}
    </div>
  );
}
